import {
    AdminClient,
    IS_EDITOR_PREVIEW_PARAM,
    IS_PREVIEW_PARAM,
    NO_PIXELS_PARAM,
} from '@storefronts/common-types'
import { R } from '../api/actions'

export const getCrowlinkUrl = (client: AdminClient, crowLinkHash: string) => {
    const path = `crowlink/${crowLinkHash}`
    if (!client.lpDomain) {
        // if lpDomain is empty, assume we are serving via app proxy
        return `https://${client.mainDomain}/apps/blackcrow/storefronts/${path}`
    }
    return `https://${client.lpDomain}/${path}`
}

export const getCanonicalLandingPageUrl = (
    client: AdminClient,
    landingPageHash: string,
) => {
    const path = `landing/${landingPageHash}`
    if (!client.lpDomain) {
        // if lpDomain is empty, assume we are serving via app proxy
        return `https://${client.mainDomain}/apps/blackcrow/storefronts/${path}`
    }
    return `https://${client.lpDomain}/${path}`
}

export const getPreviewPageUrl = (landingPageHash: string) => {
    return `${process.env.NEXT_PUBLIC_SHOPPER_FRONTEND_BASEURL}/preview/${landingPageHash}`
}

export const getLandPagePreviewUrl = (
    landingPageHash: string,
    options?: { showPreviewBanner?: boolean; loadPixels?: boolean },
) => {
    const baseUrl = `${process.env.NEXT_PUBLIC_SHOPPER_FRONTEND_BASEURL}/land/${landingPageHash}?${IS_PREVIEW_PARAM}=${!!options?.showPreviewBanner}&${NO_PIXELS_PARAM}=${!options?.loadPixels}`
    return baseUrl
}

/**
 * Gets the landing page preview URL for use in iframes.
 * Uses the admin-frontend proxy route so frame-ancestors can be set correctly
 * for the same environment (especially important for Vercel preview environments).
 *
 * @param landingPageHash - The landing page hash
 * @param options - Preview options
 * @returns URL pointing to admin-frontend proxy route
 */
export const getLandPagePreviewUrlForIframe = (
    landingPageHash: string,
    options?: { showPreviewBanner?: boolean; loadPixels?: boolean },
) => {
    const queryParams = new URLSearchParams()
    if (options?.showPreviewBanner) {
        queryParams.set(IS_PREVIEW_PARAM, 'true')
    }
    if (!options?.loadPixels) {
        queryParams.set(NO_PIXELS_PARAM, 'true')
    }

    const queryString = queryParams.toString()
    // Use relative URL so it works with the current admin-frontend origin
    return `/preview/land/${landingPageHash}${
        queryString ? `?${queryString}` : ''
    }`
}

/**
 * Gets the admin editor preview URL for use in iframes.
 * Uses the admin-frontend route so frame-ancestors can be set correctly
 * for the same environment (especially important for Vercel preview environments).
 *
 * @returns URL pointing to admin-frontend route
 */
export const getAdminEditorPreviewUrlForIframe = () => {
    // Use relative URL so it works with the current admin-frontend origin
    return `/admin-editor-preview?${IS_EDITOR_PREVIEW_PARAM}=true`
}

/**
 * Gets the admin product preview URL for use in iframes.
 * Uses the admin-frontend route so frame-ancestors can be set correctly
 * for the same environment (especially important for Vercel preview environments).
 *
 * @returns URL pointing to admin-frontend route
 */
export const getAdminProductPreviewUrlForIframe = () => {
    // Use relative URL so it works with the current admin-frontend origin
    return '/admin-product-preview'
}

export enum AdPlatform {
    META = 'meta',
    GOOGLE = 'google',
    TIKTOK = 'tiktok',
    PINTEREST = 'pinterest',
    SNAPCHAT = 'snapchat',
    REDDIT = 'reddit',
    ROKT = 'rokt',
    APPLOVIN = 'applovin',
    CRITEO = 'criteo',
    DIRECT = 'direct',
}

export type PlatformDisplayNames = Record<AdPlatform, string>
export type PlatformUrls = Record<AdPlatform, string>
export type PlatformAdUrls = Record<AdPlatform, string>

export const platformDisplayNames: PlatformDisplayNames = {
    [AdPlatform.META]: 'Meta',
    [AdPlatform.GOOGLE]: 'Google',
    [AdPlatform.TIKTOK]: 'TikTok',
    [AdPlatform.PINTEREST]: 'Pinterest',
    [AdPlatform.SNAPCHAT]: 'Snapchat',
    [AdPlatform.REDDIT]: 'Reddit',
    [AdPlatform.ROKT]: 'Rokt / Aftersell',
    [AdPlatform.APPLOVIN]: 'AppLovin / Axon',
    [AdPlatform.CRITEO]: 'Criteo',
    [AdPlatform.DIRECT]: 'Direct Link',
}

export const platformAdUrls: PlatformAdUrls = {
    [AdPlatform.META]: 'https://business.facebook.com/commerce_manager',
    [AdPlatform.GOOGLE]: 'https://ads.google.com',
    [AdPlatform.TIKTOK]: 'https://ads.tiktok.com',
    [AdPlatform.PINTEREST]: 'https://ads.pinterest.com',
    [AdPlatform.SNAPCHAT]: 'https://ads.snapchat.com',
    [AdPlatform.REDDIT]: 'https://www.reddit.com/advertising',
    [AdPlatform.ROKT]: 'https://my.rokt.com/',
    [AdPlatform.APPLOVIN]: 'https://dash.applovin.com/',
    [AdPlatform.CRITEO]: 'https://www.criteo.com/login/',
    [AdPlatform.DIRECT]: '',
}

interface AdPlatformParamsWithUTM {
    bc_source: string
    bc_campaign: string
    bc_adid: string
}

type AdPlatformParams = AdPlatformParamsWithUTM | Record<string, never>

const KLAVIYO_UTM_PARAMS: AdPlatformParamsWithUTM = {
    bc_source: 'klaviyo',
    bc_campaign: '{{ campaign.name }}',
    bc_adid: '{{ email_message.id }}',
}

const ATTENTIVE_UTM_PARAMS: AdPlatformParamsWithUTM = {
    bc_source: 'attentive',
    bc_campaign: '{{message.messageName}}',
    bc_adid: '{{message.messageId}}',
}

const utmParamsToQueryString = (params: AdPlatformParamsWithUTM) =>
    Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

const AD_PLATFORM_PARAMS: Record<AdPlatform, AdPlatformParams> = {
    [AdPlatform.META]: {
        bc_source: '{{site_source_name}}',
        bc_campaign: '{{campaign.id}}',
        bc_adid: '{{ad.id}}',
    },
    [AdPlatform.GOOGLE]: {
        bc_source: 'google',
        bc_campaign: '{campaignid}',
        bc_adid: '{creative}',
    },
    [AdPlatform.TIKTOK]: {
        bc_source: 'tiktok',
        bc_campaign: '__CAMPAIGN_ID__',
        bc_adid: '__CID__',
    },
    [AdPlatform.PINTEREST]: {
        bc_source: 'pinterest',
        bc_campaign: '{campaignid}',
        bc_adid: '{adid}',
    },
    [AdPlatform.SNAPCHAT]: {
        bc_source: 'snapchat',
        bc_campaign: '{{campaign.name}}',
        bc_adid: '{{ad.id}}',
    },
    [AdPlatform.REDDIT]: {
        bc_source: 'reddit',
        bc_campaign: '{{CAMPAIGN_ID}}',
        bc_adid: '{{AD_ID}}',
    },
    [AdPlatform.ROKT]: {
        bc_source: 'rokt',
        bc_campaign: '{{offer_id}}',
        bc_adid: '{{placement_id}}',
    },
    [AdPlatform.APPLOVIN]: {},
    [AdPlatform.CRITEO]: {
        bc_source: 'criteo',
        bc_campaign: '%%campaignid%%',
        bc_adid: '%%creativeid%%',
    },
    [AdPlatform.DIRECT]: {},
}

export enum OwnedPlatform {
    KLAVIYO = 'klaviyo',
    ATTENTIVE = 'attentive',
    POSTSCRIPT = 'postscript',
    YOTPO = 'yotpo',
    OTHER = 'other',
}

export type UrlPlatform = AdPlatform | OwnedPlatform

export const ownedPlatformDisplayNames: Record<OwnedPlatform, string> = {
    [OwnedPlatform.KLAVIYO]: 'Klaviyo',
    [OwnedPlatform.ATTENTIVE]: 'Attentive',
    [OwnedPlatform.POSTSCRIPT]: 'Postscript',
    [OwnedPlatform.YOTPO]: 'Yotpo',
    [OwnedPlatform.OTHER]: 'Other',
}

export const ownedPlatformUrls: Record<OwnedPlatform, string | undefined> = {
    [OwnedPlatform.KLAVIYO]: 'https://www.klaviyo.com/login',
    [OwnedPlatform.ATTENTIVE]: 'https://ui.attentivemobile.com/signin',
    [OwnedPlatform.POSTSCRIPT]: 'https://app.postscript.io/login',
    [OwnedPlatform.YOTPO]: 'https://www.yotpo.com/',
    [OwnedPlatform.OTHER]: undefined,
}

export const allPlatformDisplayNames: Record<UrlPlatform, string> = {
    [AdPlatform.META]: platformDisplayNames[AdPlatform.META],
    [AdPlatform.GOOGLE]: platformDisplayNames[AdPlatform.GOOGLE],
    [AdPlatform.TIKTOK]: platformDisplayNames[AdPlatform.TIKTOK],
    [AdPlatform.PINTEREST]: platformDisplayNames[AdPlatform.PINTEREST],
    [AdPlatform.SNAPCHAT]: platformDisplayNames[AdPlatform.SNAPCHAT],
    [AdPlatform.REDDIT]: platformDisplayNames[AdPlatform.REDDIT],
    [AdPlatform.ROKT]: platformDisplayNames[AdPlatform.ROKT],
    [AdPlatform.APPLOVIN]: platformDisplayNames[AdPlatform.APPLOVIN],
    [AdPlatform.CRITEO]: platformDisplayNames[AdPlatform.CRITEO],
    [OwnedPlatform.KLAVIYO]: ownedPlatformDisplayNames[OwnedPlatform.KLAVIYO],
    [OwnedPlatform.ATTENTIVE]:
        ownedPlatformDisplayNames[OwnedPlatform.ATTENTIVE],
    [OwnedPlatform.POSTSCRIPT]:
        ownedPlatformDisplayNames[OwnedPlatform.POSTSCRIPT],
    [OwnedPlatform.YOTPO]: ownedPlatformDisplayNames[OwnedPlatform.YOTPO],
    [OwnedPlatform.OTHER]: ownedPlatformDisplayNames[OwnedPlatform.OTHER],
    [AdPlatform.DIRECT]: platformDisplayNames[AdPlatform.DIRECT],
}

const OWNED_PLATFORM_VALUES = new Set<string>(Object.values(OwnedPlatform))

export const isOwnedPlatform = (
    platform: UrlPlatform,
): platform is OwnedPlatform => OWNED_PLATFORM_VALUES.has(platform)

const OwnedPlatformToUrlQueryParam = {
    [OwnedPlatform.KLAVIYO]: 'eventId={{ email|sha_256 }}',
    [OwnedPlatform.ATTENTIVE]:
        "{{subscriber.custom['blackcrow_query_params']}}",
    [OwnedPlatform.POSTSCRIPT]:
        "{{subscriber.custom_properties.get('blackcrow_query_params')}}",
    [OwnedPlatform.YOTPO]: '{{profile.custom.blackcrow_query_params}}',
    [OwnedPlatform.OTHER]: undefined,
}

const OwnedPlatformToUtmQueryParam: Partial<Record<OwnedPlatform, string>> = {
    [OwnedPlatform.KLAVIYO]: utmParamsToQueryString(KLAVIYO_UTM_PARAMS),
    [OwnedPlatform.ATTENTIVE]: utmParamsToQueryString(ATTENTIVE_UTM_PARAMS),
}

export enum OwnedMessageType {
    WELCOME = 'Welcome',
    ABANDONMENT = 'Abandonment',
    REPURCHASE = 'Repurchase',
    CAMPAIGN = 'Campaign',
    OTHER = 'Other',
}

export enum OwnedFlowtype {
    // Abandonment flows
    BROWSE = 'Browse Abandonment',
    CART = 'Cart Abandonment',
    CHECKOUT = 'Checkout Abandonment',
    // Repurchase flows
    CROSS_SELL = 'Cross Sell',
    REPLENISHMENT = 'Replenishment',
    SUBSCRIBE = 'Subscribe',
    // Other flows
    WELCOME = 'Welcome',
    CAMPAIGN = 'Campaign',
    OTHER = 'Other',
}

const OwnedFlowtypeToUrlQueryParam = {
    // Abandonment flows
    [OwnedFlowtype.BROWSE]: 'flowType=ViewedProduct',
    [OwnedFlowtype.CART]: 'flowType=AddToCart',
    [OwnedFlowtype.CHECKOUT]: 'flowType=CheckoutStarted',
    // Repurchase flows
    [OwnedFlowtype.CROSS_SELL]: 'flowType=CrossSell',
    [OwnedFlowtype.REPLENISHMENT]: 'flowType=Replenishment',
    [OwnedFlowtype.SUBSCRIBE]: 'flowType=Subscribe',
    // Other flows
    [OwnedFlowtype.WELCOME]: 'flowType=Welcome',
    [OwnedFlowtype.CAMPAIGN]: 'flowType=Campaign',
    [OwnedFlowtype.OTHER]: 'flowType=Other',
}

/**
 * Converts a message type to its corresponding flow type URL query parameter.
 * For message types with fixed flow types (Welcome, Campaign, Other), this returns
 * the appropriate URL parameter string. For other message types, returns undefined.
 */
function getFlowTypeUrlParamFromMessageType(
    messageType: OwnedMessageType | undefined,
): string | undefined {
    switch (messageType) {
        case OwnedMessageType.WELCOME:
            return OwnedFlowtypeToUrlQueryParam[OwnedFlowtype.WELCOME]
        case OwnedMessageType.CAMPAIGN:
            return OwnedFlowtypeToUrlQueryParam[OwnedFlowtype.CAMPAIGN]
        case OwnedMessageType.OTHER:
            return OwnedFlowtypeToUrlQueryParam[OwnedFlowtype.OTHER]
        default:
            return undefined
    }
}

export const getOwnedLinkUrlWithParams = (
    client: AdminClient,
    crowLinkHash: string,
    platform: OwnedPlatform,
    messageType?: OwnedMessageType,
    flowtype?: OwnedFlowtype,
) => {
    const baseUrl = getCrowlinkUrl(client, crowLinkHash)
    // Not encoding the query params since the platformParam is a template
    // string that will be replaced by the platform.

    // TBD What to do with the flowtypeParam (should it be encoded?)
    const params = [
        OwnedPlatformToUrlQueryParam[platform],
        OwnedPlatformToUtmQueryParam[platform],
        flowtype
            ? OwnedFlowtypeToUrlQueryParam[flowtype]
            : getFlowTypeUrlParamFromMessageType(messageType),
    ]
        .filter(Boolean)
        .join('&')
    return `${baseUrl}${!!params ? `${baseUrl.includes('?') ? '&' : '?'}${params}` : ''}`
}

export const getCrowLinkUrlWithParams = (
    client: AdminClient,
    crowLinkHash: string,
    platform: AdPlatform = AdPlatform.META,
) => {
    const baseUrl = getCrowlinkUrl(client, crowLinkHash)
    const params = AD_PLATFORM_PARAMS[platform]

    if (!Object.keys(params).length) {
        return baseUrl
    }

    const queryParams = Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

    return `${baseUrl}?${queryParams}`
}

export const getCrowLinksByLandingPage = async (landingPageHash: string) => {
    try {
        return await R.getCrowLinksByLandingPage(landingPageHash)
    } catch (error) {
        console.error('Error getting crow links:', error)
        return []
    }
}
